import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Printer, ArrowLeft, BookOpen } from "lucide-react";
import { MathContent } from "@/components/math/math-content";
import { TheoryView } from "@/components/theory-view";

type Exercise = {
  id: string;
  skillKey?: string | null;
  title: string;
  instructions: string | null;
  theory: string | null;
  problemText: string;
  expectedAnswer: string | null;
  hint: string | null;
  choices?: string[] | null;
  skill: { name: string } | null;
};

type Session = {
  id: string;
  createdAt: string;
  student: {
    name: string;
  };
  exercises: Exercise[];
};

/**
 * A block is one heading on the paper sheet. An exercise is one question, so
 * printing each as its own numbered section gives eighteen headings repeating
 * the same skill name. Consecutive exercises of the same skill are merged
 * instead — only consecutive ones, so the sheet keeps the order the session was
 * planned in.
 */
type Block = {
  key: string;
  title: string;
  instructions: string | null;
  theory: string | null;
  questions: Exercise[];
};

function toBlocks(exercises: Exercise[]): Block[] {
  const blocks: Block[] = [];

  for (const exercise of exercises) {
    const key = exercise.skillKey || exercise.title;
    const previous = blocks[blocks.length - 1];

    if (previous && previous.key === key) {
      previous.questions.push(exercise);
      previous.theory ||= exercise.theory;
      previous.instructions ||= exercise.instructions;
      continue;
    }

    blocks.push({
      key,
      title: exercise.title,
      instructions: exercise.instructions,
      theory: exercise.theory,
      questions: [exercise],
    });
  }

  return blocks;
}

/**
 * Choices that are all bare letters ("A", "B", "C") are labels pointing at
 * options already drawn inside the problem text — listing them again prints
 * "A) A  B) B". Anything else is real content the child cannot answer without.
 */
function printableChoices(choices?: string[] | null): string[] | null {
  if (!choices || choices.length === 0) return null;
  if (choices.every((choice) => /^[A-Za-z]$/.test(choice.trim()))) return null;

  return choices;
}

const CHOICE_LABELS = "ABCDEFGH";

export function PrintWorksheet({ session }: { session: Session }) {
  const [showTheory, setShowTheory] = useState(false);
  const [showHints, setShowHints] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const blocks = toBlocks(session.exercises);
  const hasTheory = blocks.some((b) => b.theory);
  const hasHints = blocks.some((b) => b.questions.some((q) => q.hint));

  // Problems are numbered once across the whole sheet, so "task 7" means the
  // same thing to the parent reading and the child writing.
  let problemNumber = 0;

  return (
    <>
      {/* Print controls - hidden when printing */}
      <div className="no-print mb-6 flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/sessions/${session.id}`}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Spausdinimo peržiūra</h1>
          <p className="text-muted-foreground">
            Peržiūrėkite darbo lapą prieš spausdinant
          </p>
        </div>
        {(hasTheory || hasHints) && (
          <div className="flex items-center gap-4 mr-4">
            {hasTheory && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-theory"
                  checked={showTheory}
                  onCheckedChange={(checked) => setShowTheory(checked === true)}
                />
                <Label htmlFor="show-theory" className="text-sm cursor-pointer">
                  Teorija
                </Label>
              </div>
            )}
            {hasHints && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-hints"
                  checked={showHints}
                  onCheckedChange={(checked) => setShowHints(checked === true)}
                />
                <Label htmlFor="show-hints" className="text-sm cursor-pointer">
                  Užuominos
                </Label>
              </div>
            )}
          </div>
        )}
        {/* The checkbox beside this one boxes theory into the worksheet as a
            10pt footnote above each block. This is the other thing you might
            want: the same material as a sheet to read from. */}
        {hasTheory && (
          <Button variant="outline" asChild>
            <Link to={`/sessions/${session.id}/theory/print`}>
              <BookOpen className="mr-2 h-4 w-4" />
              Tik teorija
            </Link>
          </Button>
        )}
        <Button onClick={handlePrint}>
          <Printer className="mr-2 h-4 w-4" />
          Spausdinti
        </Button>
      </div>

      {/* Print content */}
      <div
        className="print-content bg-white p-8 max-w-4xl mx-auto"
        style={{ backgroundColor: 'white', color: 'black', padding: '0' }}
      >
        {/* Header */}
        <div className="print-header mb-8" style={{ borderBottom: '1px solid black', paddingBottom: '0.3rem', marginBottom: '0.5rem' }}>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-1" style={{ color: 'black', fontSize: '16pt', marginBottom: '0.1rem' }}>Darbo lapas</h1>
              <p className="text-lg" style={{ color: 'black', fontSize: '12pt' }}>
                Mokinys: <strong>{session.student.name}</strong>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600" style={{ color: '#666', fontSize: '10pt' }}>
                Data:{" "}
                {new Date(session.createdAt).toLocaleDateString("lt-LT", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Exercises */}
        <div className="space-y-8" style={{ marginTop: '0.5rem' }}>
          {(() => {
            const shownTheories = new Set<string>();
            return blocks.map((block, blockIndex) => {
              const theoryAlreadyShown = block.theory ? shownTheories.has(block.theory) : true;
              if (block.theory) shownTheories.add(block.theory);
              return (
            <div key={`${block.key}-${blockIndex}`} style={{ marginBottom: '0.6rem' }}>
              <div className="mb-4" style={{ marginBottom: '0.3rem' }}>
                <h2 className="text-lg font-semibold" style={{ color: 'black', fontSize: '13pt' }}>
                  {block.title}
                </h2>
                {block.instructions && (
                  <p className="text-gray-600 mt-1" style={{ color: '#666', marginTop: '0.1rem', fontSize: '11pt' }}>{block.instructions}</p>
                )}
                {showTheory && block.theory && !theoryAlreadyShown && (
                  <div style={{
                    marginTop: '0.4rem',
                    padding: '0.5rem',
                    backgroundColor: '#f0f7ff',
                    border: '1px solid #cce0ff',
                    borderRadius: '4px',
                    fontSize: '10pt'
                  }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '0.2rem', color: '#1e40af' }}>📖 Teorija:</p>
                    <TheoryView
                      html={block.theory}
                      className="[&]:text-[#1e3a5f]"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-6" style={{ marginTop: '0.3rem' }}>
                {block.questions.map((problem) => {
                  problemNumber += 1;
                  const choices = printableChoices(problem.choices);
                  return (
                  <div key={problem.id} className="print-problem" style={{ borderBottom: '1px dashed #ccc', paddingBottom: '0.3rem', marginBottom: '0.4rem' }}>
                    <div className="flex gap-3" style={{ gap: '0.5rem' }}>
                      <span className="font-medium text-gray-500" style={{ color: '#666', fontSize: '11pt' }}>
                        {problemNumber}.
                      </span>
                      <div className="flex-1">
                        <MathContent html={problem.problemText} className="mb-3" style={{ color: 'black', marginBottom: '0.2rem', fontSize: '11pt' }} />
                        {choices && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1.2rem', marginBottom: '0.2rem' }}>
                            {choices.map((choice, choiceIndex) => (
                              <div
                                key={`${problem.id}-choice-${choiceIndex}`}
                                style={{ display: 'flex', gap: '0.25rem', alignItems: 'baseline' }}
                              >
                                <span style={{ color: '#666', fontSize: '10pt' }}>
                                  {CHOICE_LABELS[choiceIndex] ?? choiceIndex + 1})
                                </span>
                                <MathContent
                                  html={choice}
                                  style={{ color: 'black', fontSize: '10pt' }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {showHints && problem.hint && (
                          <div style={{
                            marginTop: '0.2rem',
                            marginBottom: '0.2rem',
                            padding: '0.3rem 0.5rem',
                            backgroundColor: '#fffbeb',
                            border: '1px solid #fde68a',
                            borderRadius: '3px',
                            fontSize: '9pt',
                            color: '#92400e'
                          }}>
                            <span style={{ fontWeight: 'bold' }}>💡 Užuomina:</span> {problem.hint}
                          </div>
                        )}
                        <div className="print-answer-line" style={{ borderBottom: '1px solid black', minHeight: '1.2rem', marginTop: '0.2rem' }}>
                          <span className="text-sm text-gray-400" style={{ color: '#999', fontSize: '9pt' }}>
                            Atsakymas:
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
              );
            });
          })()}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-4 border-t text-center text-sm text-gray-400" style={{ borderTop: '1px solid #ccc', color: '#999', marginTop: '0.5rem', paddingTop: '0.3rem', fontSize: '9pt' }}>
          <p>Scio - Mokinių įgūdžių sekimo sistema</p>
        </div>
      </div>
    </>
  );
}
